import { useCallback, useEffect, useState } from "react";
import { Loader2, Utensils } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtUntil = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// One tap: AI assistant + phone receptionist cover the desk for an hour, then revert.
export const LunchCoverButton = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { const r = await api.get("/workshop/lunch-cover"); setSt(r.data); } catch { /* keep last */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post(st?.active ? "/workshop/lunch-cover/end" : "/workshop/lunch-cover/start", { minutes: 60 });
      setSt(r.data);
      toast.success(r.data.active
        ? `AI is covering the desk until ${fmtUntil(r.data.until)} — texts and calls answered, anything commercial handed back to you`
        : "Welcome back — AI cover switched back to how it was");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change lunch cover");
    } finally { setBusy(false); }
  };

  if (!st) return null;
  const active = st.active;
  return (
    <button
      type="button" onClick={toggle} disabled={busy}
      className={`min-h-[52px] px-4 rounded-xl font-bold text-sm inline-flex items-center gap-2.5 shrink-0 transition-colors duration-150 disabled:opacity-60 ${
        active ? "bg-amber-400 text-slate-950 hover:bg-amber-300" : "bg-slate-800 text-slate-200 hover:bg-slate-700"
      }`}
      data-testid="lunch-cover-btn"
      title={active ? "Tap when you're back — AI cover reverts to its normal setting" : "AI answers texts and calls for the next hour, then switches back"}
    >
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Utensils className="w-5 h-5" />}
      {active ? (
        <span className="text-left leading-tight">
          <span className="block">I'm back</span>
          <span className="block text-[11px] font-semibold opacity-80" data-testid="lunch-cover-until">AI covering until {fmtUntil(st.until)} · {st.minutes_left} min</span>
        </span>
      ) : "We're at lunch"}
    </button>
  );
};
